// layouts/catalogos/parametros/editar-parametro.jsx
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";

import FormPage from "ui/FormPage";
import { PrimaryButton, OutlineButton } from "ui/Buttons";
import FormTextField from "ui/FormTextField";

import { getParametro as getParametroById, updateParametro } from "../services/parametros";
import { listFormatos } from "layouts/catalogos/services/formatos";

/* ---------------- helpers ---------------- */
function extractErrMessages(err) {
  const out = [];
  const d = err?.response?.data ?? err;
  const push = (m) => m && out.push(String(m));

  if (typeof d === "string") push(d);
  else if (Array.isArray(d)) d.forEach((it) => push(it?.message || JSON.stringify(it)));
  else if (d?.errors && Array.isArray(d.errors))
    d.errors.forEach((it) =>
      push(
        it?.message ||
          `${it?.instancePath || it?.path || ""} ${it?.message || ""}`.trim()
      )
    );
  else if (d?.message) push(d.message);
  else push(err?.message || "Error desconocido");

  return out;
}

// 👉 De valor ISO/fecha de la API → string "YYYY-MM-DD" para el <input type="date" />
const toDateInput = (iso) => {
  if (!iso) return "";
  if (typeof iso === "string" && iso.length >= 10) {
    // "2025-11-24T00:00:00.000Z" → "2025-11-24"
    return iso.slice(0, 10);
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

// 👉 De "YYYY-MM-DD" (del input) → string para API (la misma)
// MySQL se lleva bien con "2025-11-24" y lo guarda como 2025-11-24 00:00:00
const toApiDate = (yyyyMMdd) => {
  if (!yyyyMMdd) return undefined;
  return yyyyMMdd;
};

const toNum = (v) => {
  if (v === "" || v == null) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};
/* ---------------- component ---------------- */

export default function EditarParametro() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);

  // form state
  const [formatId, setFormatId] = useState("");
  const [since, setSince] = useState(""); // "YYYY-MM-DD"
  const [until, setUntil] = useState(""); // "YYYY-MM-DD"
  const [gold, setGold] = useState("");
  const [platinum, setPlatinum] = useState("");
  const [diamond, setDiamond] = useState("");
  const [createdAt, setCreatedAt] = useState("");

  // data para select de formatos
  const [formatos, setFormatos] = useState([]);
  const [loadingFormatos, setLoadingFormatos] = useState(true);

  const goldNum = toNum(gold);
  const platinumNum = toNum(platinum);
  const diamondNum = toNum(diamond);

  const isValid = useMemo(() => {
    if (!String(formatId).trim()) return false;
    if (!since || !until) return false;
    if (!Number.isFinite(goldNum) || goldNum <= 0) return false;
    if (!Number.isFinite(platinumNum) || platinumNum <= 0) return false;
    if (!Number.isFinite(diamondNum) || diamondNum <= 0) return false;
    return true;
  }, [formatId, since, until, goldNum, platinumNum, diamondNum]);

  // carga formatos + parámetro
  useEffect(() => {
    let cancel = false;

    (async () => {
      setLoading(true);
      setErrors([]);

      try {
        // 1) formatos
        setLoadingFormatos(true);
        try {
          const res = await listFormatos();
          const rows =
            Array.isArray(res?.rows) ? res.rows :
            Array.isArray(res) ? res :
            Array.isArray(res?.data) ? res.data :
            Array.isArray(res?.page) ? res.page : [];
          if (!cancel) setFormatos(rows);
        } finally {
          if (!cancel) setLoadingFormatos(false);
        }

        // 2) parámetro actual
        const it = await getParametroById(id);
        if (cancel) return;

        setFormatId(it?.format_id ?? "");
        setSince(toDateInput(it?.since));
        setUntil(toDateInput(it?.until));
        setGold(it?.gold ?? "");
        setPlatinum(it?.platinum ?? "");
        setDiamond(it?.diamond ?? "");
        setCreatedAt(it?.created_at ?? it?.createdAt ?? "");
      } catch (e) {
        if (!cancel) setErrors(extractErrMessages(e));
      } finally {
        if (!cancel) setLoading(false);
      }
    })();

    return () => {
      cancel = true;
    };
  }, [id]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) return;

    setSaving(true);
    setErrors([]);

    try {
      const payload = {
        format_id: toNum(formatId),
        since: toApiDate(since),
        until: toApiDate(until),
        gold: goldNum,
        platinum: platinumNum,
        diamond: diamondNum,
      };

      // limpia undefined/null
      Object.keys(payload).forEach((k) => {
        if (payload[k] == null || payload[k] === "") delete payload[k];
      });

      await updateParametro(id, payload);
      navigate("/catalogos/parametros", { replace: true });
    } catch (e2) {
      setErrors(extractErrMessages(e2));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormPage
      title="Editar parámetro"
      onBack={() => navigate("/catalogos/parametros")}
      pageMaxWidth={1100}
      contentMaxWidth={900}
      actions={
        <>
          <PrimaryButton
            type="submit"
            form="form-param"
            disabled={!isValid || saving || loading}
          >
            {saving ? "Guardando..." : "Guardar"}
          </PrimaryButton>
          <OutlineButton onClick={() => navigate("/catalogos/parametros")}>
            Cancelar
          </OutlineButton>
        </>
      }
    >
      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.map((m, i) => (
            <div key={i}>{m}</div>
          ))}
        </Alert>
      )}

      <form id="form-param" onSubmit={onSubmit}>
        <Grid container spacing={2}>
          {/* Formato (format_id) */}
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Formato*"
              value={String(formatId ?? "")}
              onChange={(e) => setFormatId(e.target.value)}
              required
              disabled={loadingFormatos || loading}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="" disabled>
                {loadingFormatos ? "Cargando formatos…" : "Selecciona un formato"}
              </MenuItem>
              {formatos.map((f) => {
                const idFmt = f.id ?? f._id;
                const name = f.name ?? f.nombre ?? `Formato ${idFmt}`;
                const code = f.code ?? f.codigo;
                return (
                  <MenuItem key={idFmt} value={String(idFmt)}>
                    {name}
                    {code ? ` (${code})` : ""}
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>

          {/* Rango de fechas */}
          <Grid item xs={12} md={3}>
            <FormTextField
              label="Desde*"
              type="date"
              value={since}
              onChange={(e) => setSince(e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormTextField
              label="Hasta*"
              type="date"
              value={until}
              onChange={(e) => setUntil(e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
              disabled={loading}
            />
          </Grid>

          {/* Montos */}
          <Grid item xs={12} md={4}>
            <FormTextField
              label="Gold*"
              type="number"
              value={gold}
              onChange={(e) => setGold(e.target.value)}
              inputProps={{ min: 1, step: 1 }}
              required
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormTextField
              label="Platinum*"
              type="number"
              value={platinum}
              onChange={(e) => setPlatinum(e.target.value)}
              inputProps={{ min: 1, step: 1 }}
              required
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormTextField
              label="Diamond*"
              type="number"
              value={diamond}
              onChange={(e) => setDiamond(e.target.value)}
              inputProps={{ min: 1, step: 1 }}
              required
              disabled={loading}
            />
          </Grid>

          {/* Solo lectura: fecha creación */}
          <Grid item xs={12} md={6}>
            <FormTextField label="Creado" value={createdAt || ""} disabled />
          </Grid>
        </Grid>
      </form>
    </FormPage>
  );
}
